import { useEffect, useId, useRef, useState } from 'react';
import { NAME_MAX, nameKey } from '../shared/constants';
import { EditPlayerSheet } from '../components/EditPlayerSheet';
import { Avatar, Sheet, teamAvatarFill } from '../components/ui';
import { api } from '../lib/api';
import { nameErrors, updateDraft, useDraft, type DraftPlayer } from '../lib/draft';
import { toAvatarBlob } from '../lib/image';
import { navigate } from '../lib/router';
import {
  editSavedPlayer,
  forgetSavedPlayer,
  forgetSavedTeam,
  getLastPlayers,
  getSavedPlayers,
  getSavedTeams,
  renameSavedTeam,
  teamSetKey,
  type SavedPlayer,
  type SavedTeam,
} from '../lib/store';
import { toast } from '../lib/toast';
import { BackButton, ScreenHeader } from './shared';

function setPlayer(i: number, patch: Partial<DraftPlayer>) {
  updateDraft((d) => ({ ...d, players: d.players.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));
}

function PlayerCard({
  index,
  player,
  error,
  showError,
  photos,
  onPickSaved,
}: {
  index: number;
  player: DraftPlayer;
  error: string | null;
  showError: boolean;
  /** False when the server has no photo storage. */
  photos: boolean;
  onPickSaved: () => void;
}) {
  const id = useId();
  const gallery = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  // Local preview of a just-picked photo; ignored once the slot shows a different player/photo.
  const [preview, setPreview] = useState<{ url: string; key: string | null } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => () => void (preview && URL.revokeObjectURL(preview.url)), [preview]);
  const previewUrl = preview && (busy || preview.key === player.avatarKey) ? preview.url : null;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const blob = await toAvatarBlob(file);
      const url = URL.createObjectURL(blob);
      setPreview({ url, key: null });
      const form = new FormData();
      form.append('file', blob, blob.type === 'image/webp' ? 'avatar.webp' : 'avatar.jpg');
      const { key } = await api<{ key: string }>('/avatars', { method: 'POST', form });
      setPreview({ url, key });
      setPlayer(index, { avatarKey: key });
    } catch (err) {
      setPreview(null);
      toast(err instanceof Error ? err.message : 'Could not upload the photo.', 'error');
    } finally {
      setBusy(false);
      if (gallery.current) gallery.current.value = '';
      if (camera.current) camera.current.value = '';
    }
  };

  const invalid = showError && Boolean(error);
  const hasPhoto = Boolean(player.avatarKey || previewUrl);
  return (
    <li className="animate-rise flex items-center gap-3 py-2.5" style={{ animationDelay: `${index * 50}ms` }}>
      <div className="relative shrink-0">
        <button
          type="button"
          onClick={() => photos && gallery.current?.click()}
          disabled={!photos || busy}
          aria-label={photos ? `Choose a photo for player ${index + 1}` : `Player ${index + 1}`}
          className="block rounded-full transition enabled:hover:brightness-95 disabled:cursor-default"
        >
          <Avatar
            name={player.name || `P${index + 1}`}
            avatarKey={player.avatarKey}
            src={previewUrl}
            size={48}
            fill={player.name ? undefined : '#d8cfb8'}
            ring={player.name ? 'var(--color-gold)' : undefined}
          />
        </button>
        {photos && (
          <button
            type="button"
            onClick={() => camera.current?.click()}
            disabled={busy}
            aria-label={`Take a photo of player ${index + 1}`}
            className="absolute -right-1 -bottom-1 grid h-6 w-6 place-items-center rounded-full bg-ink text-[11px] text-cream shadow ring-2 ring-cream"
          >
            📷
          </button>
        )}
        {hasPhoto && !busy && (
          <button
            type="button"
            onClick={() => {
              setPreview(null);
              setPlayer(index, { avatarKey: null });
            }}
            aria-label={`Remove photo of player ${index + 1}`}
            className="absolute -top-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-white text-[10px] text-red-700 shadow ring-1 ring-ink/15"
          >
            ✕
          </button>
        )}
        {busy && (
          <span className="absolute inset-0 grid place-items-center rounded-full bg-ink/60 text-white" role="status">
            <span className="animate-spin" aria-hidden>
              ♣
            </span>
            <span className="sr-only">Uploading photo</span>
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="mb-1 block text-[11px] font-bold tracking-wider text-ink-soft/80 uppercase">
          Player {index + 1}
        </label>
        <div className="flex gap-2">
          <input
            id={id}
            dir="auto"
            value={player.name}
            maxLength={NAME_MAX}
            onChange={(e) => setPlayer(index, { name: e.target.value, playerId: undefined })}
            placeholder="Name"
            autoComplete="off"
            enterKeyHint="next"
            aria-invalid={invalid}
            aria-describedby={`${id}-hint`}
            className={`field name min-w-0 flex-1 text-base font-semibold ${invalid ? 'border-red-600 ring-2 ring-red-600/30' : ''}`}
          />
          <button
            type="button"
            onClick={onPickSaved}
            aria-label={`Pick a saved player for player ${index + 1}`}
            title="Saved players"
            className="grid h-[46px] w-11 shrink-0 place-items-center rounded-xl bg-gold/15 text-lg text-gold ring-1 ring-gold/40 transition hover:bg-gold/25"
          >
            ★
          </button>
        </div>
        {(invalid || player.name.length > NAME_MAX - 6) && (
          <p id={`${id}-hint`} className={`mt-1 text-xs ${invalid ? 'font-semibold text-red-700' : 'text-ink-soft/70'}`}>
            {invalid ? error : `${player.name.length}/${NAME_MAX}`}
          </p>
        )}
      </div>
      <input ref={gallery} type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} aria-hidden tabIndex={-1} />
      <input ref={camera} type="file" accept="image/*" capture="user" hidden onChange={(e) => onFile(e.target.files?.[0])} aria-hidden tabIndex={-1} />
    </li>
  );
}

export function Setup() {
  const draft = useDraft();
  const errors = nameErrors(draft.players);
  const valid = errors.every((e) => e === null);
  // Empty-name errors appear only after the user leaves a field; others appear immediately.
  const [touched, setTouched] = useState(false);
  const [pickFor, setPickFor] = useState<number | null>(null);
  const [saved, setSaved] = useState<SavedPlayer[]>(getSavedPlayers);
  const [editing, setEditing] = useState<SavedPlayer | null>(null);
  const taken = new Set(draft.players.map((p) => nameKey(p.name)));
  const [photos, setPhotos] = useState(false);
  const last = getLastPlayers();
  const [savedTeams, setSavedTeams] = useState<SavedTeam[]>(getSavedTeams);
  const [renaming, setRenaming] = useState<SavedTeam | null>(null);
  const [renameLabel, setRenameLabel] = useState('');
  const slotOf = (name: string) => draft.players.findIndex((p) => nameKey(p.name) === nameKey(name));

  /** Fills players, seats and team names/colours from a saved lineup, then jumps to Teams. */
  const useTeamSet = (t: SavedTeam) => {
    updateDraft((d) => ({
      ...d,
      players: t.players.map((p) => ({ name: p.name, avatarKey: p.avatarKey, playerId: p.playerId })),
      seatOrder: [0, 1, 2, 3],
      teams: t.teams,
    }));
    navigate('/new/teams');
  };

  const saveEdit = (oldName: string, patch: { name: string; avatarKey: string | null }) => {
    editSavedPlayer(oldName, patch);
    setSaved(getSavedPlayers());
    // If this player is already filled into a seat, keep that seat in sync.
    const slot = slotOf(oldName);
    if (slot !== -1) setPlayer(slot, { name: patch.name, avatarKey: patch.avatarKey });
    toast('Player updated', 'success');
  };

  const forgetEdit = (name: string) => {
    forgetSavedPlayer(name);
    setSaved(getSavedPlayers());
    toast(`${name} removed from saved players`, 'info');
  };

  const toggleSaved = (sp: SavedPlayer) => {
    const slot = slotOf(sp.name);
    if (slot !== -1) return setPlayer(slot, { name: '', avatarKey: null, playerId: undefined });
    const empty = draft.players.findIndex((p) => !p.name.trim());
    if (empty === -1) return toast('All 4 players are filled. Tap one to remove them first.');
    setPlayer(empty, { name: sp.name, avatarKey: sp.avatarKey, playerId: sp.playerId });
  };

  const useLastFour = () => {
    if (!last) return;
    updateDraft((d) => ({
      ...d,
      players: last.map((p) => ({ name: p.name, avatarKey: p.avatarKey, playerId: p.playerId })),
      seatOrder: [0, 1, 2, 3],
    }));
    toast('Added the players from your last match', 'success');
  };
  useEffect(() => {
    api<{ photos?: boolean }>('/health')
      .then((h) => setPhotos(Boolean(h.photos)))
      .catch(() => setPhotos(false));
  }, []);

  const online = draft.mode === 'online';
  const ready = draft.players.filter((p, i) => p.name.trim() && !errors[i]).length;

  return (
    <main className="mx-auto max-w-lg px-4 pb-32">
      <ScreenHeader
        step={online ? `1 of 2 · ${draft.variant === 'bandh' ? 'Bandh Rang' : 'Open Rang'}` : '1 of 3'}
        title="Who's playing?"
        left={<BackButton href={online ? '/' : '/home'} />}
      />

      {/* Your table: the four seats fill up as players are added. */}
      <section className="card mb-4 overflow-hidden" aria-label={`${ready} of 4 players ready`}>
        <div className="flex items-center justify-between px-4 pt-3.5">
          <h2 className="font-display text-lg leading-none font-extrabold">Your table</h2>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-extrabold tabular-nums ${ready === 4 ? 'bg-success-600 text-white' : 'bg-ink/8 text-ink-soft'}`}>
            {ready}/4 ready
          </span>
        </div>
        <ul className="grid grid-cols-4 gap-2 px-3 pt-3 pb-3.5">
          {draft.players.map((p, i) => (
            <li key={i} className="flex min-w-0 flex-col items-center gap-1 text-center">
              {p.name.trim() ? (
                <Avatar name={p.name} avatarKey={p.avatarKey} size={48} ring="var(--color-gold)" />
              ) : (
                <span className="grid h-12 w-12 place-items-center rounded-full border-2 border-dashed border-ink/20 text-xs font-bold text-ink-soft/60">P{i + 1}</span>
              )}
              <bdi dir="auto" className={`name block w-full truncate text-xs font-semibold ${p.name.trim() ? 'text-ink' : 'text-ink-soft/50'}`}>
                {p.name.trim() || 'Empty'}
              </bdi>
            </li>
          ))}
        </ul>
        <p className="border-t border-ink/8 bg-ink/[0.03] px-4 py-2 text-xs text-ink-soft">
          {online ? 'Next: seats and partners, then share the link so everyone joins on their own phone.' : 'Next: seats and partners, then spin for the first dealer.'}
        </p>
      </section>

      {savedTeams.length > 0 && (
        <section className="card mb-4 p-4" aria-labelledby="saved-teams-h">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 id="saved-teams-h" className="font-display text-lg leading-none font-extrabold">
              Saved teams
            </h2>
            <span className="text-xs text-ink-soft">Tap one to play again</span>
          </div>
          <ul className="grid gap-2">
            {savedTeams.map((t) => {
              const key = teamSetKey(t.players);
              return (
                <li key={key} className="flex items-stretch overflow-hidden rounded-2xl bg-white shadow-[0_6px_16px_-10px_rgb(0_0_0/0.5)] ring-1 ring-ink/10">
                  <button type="button" onClick={() => useTeamSet(t)} className="group flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left hover:bg-gold/5">
                    <span className="flex shrink-0 items-center" aria-hidden>
                      <span className="flex -space-x-2">
                        {[0, 2].map((i) => (
                          <Avatar key={i} name={t.players[i]!.name} avatarKey={t.players[i]!.avatarKey} size={26} ring={t.teams.A.color} fill={teamAvatarFill(t.teams.A.color)} />
                        ))}
                      </span>
                      <span className="mx-1 text-[9px] font-extrabold text-ink-soft/50">VS</span>
                      <span className="flex -space-x-2.5">
                        {[1, 3].map((i) => (
                          <Avatar key={i} name={t.players[i]!.name} avatarKey={t.players[i]!.avatarKey} size={26} ring={t.teams.B.color} fill={teamAvatarFill(t.teams.B.color)} />
                        ))}
                      </span>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-bold text-ink">{t.label}</span>
                      <span className="block truncate text-xs font-semibold">
                        <span style={{ color: t.teams.A.color }}>{t.teams.A.name}</span>
                        <span className="text-ink-soft/50"> · </span>
                        <span style={{ color: t.teams.B.color }}>{t.teams.B.name}</span>
                      </span>
                    </span>
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink text-sm font-extrabold text-cream transition group-hover:translate-x-0.5" aria-hidden>
                      →
                    </span>
                  </button>
                  <span className="flex flex-col border-l border-ink/8">
                    <button
                      type="button"
                      onClick={() => {
                        setRenaming(t);
                        setRenameLabel(t.label);
                      }}
                      aria-label={`Rename ${t.label}`}
                      className="grid flex-1 place-items-center px-2.5 text-xs text-ink-soft hover:bg-ink/5 hover:text-ink"
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        forgetSavedTeam(key);
                        setSavedTeams(getSavedTeams());
                        toast(`${t.label} removed from saved teams`, 'info');
                      }}
                      aria-label={`Forget ${t.label}`}
                      className="grid flex-1 place-items-center border-t border-ink/8 px-2.5 text-xs text-ink-soft hover:bg-red-50 hover:text-red-700"
                    >
                      ✕
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {saved.length > 0 && (
        <section className="card mb-4 p-4" aria-labelledby="saved-h">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 id="saved-h" className="font-display text-lg leading-none font-extrabold">
              Quick add
            </h2>
            {last && (
              <button type="button" className="rounded-full px-2.5 py-1 text-xs font-bold text-gold ring-1 ring-gold/50 hover:bg-gold/10" onClick={useLastFour}>
                ↺ Same 4 as last time
              </button>
            )}
          </div>
          <ul className="flex flex-wrap gap-2" aria-label="Tap a player to add or remove them">
            {saved.map((sp) => {
              const slot = slotOf(sp.name);
              const picked = slot !== -1;
              return (
                <li key={sp.name} className={`flex items-stretch overflow-hidden rounded-full transition ${picked ? 'bg-ink text-cream ring-2 ring-gold' : 'bg-white text-ink ring-1 ring-ink/12 hover:ring-ink/30'}`}>
                  <button type="button" aria-pressed={picked} onClick={() => toggleSaved(sp)} className="flex items-center gap-2 py-1 pr-2.5 pl-1 text-sm font-semibold">
                    <Avatar name={sp.name} avatarKey={sp.avatarKey} size={30} />
                    <bdi dir="auto" className="name max-w-28 truncate">
                      {sp.name}
                    </bdi>
                    {picked ? (
                      <span className="rounded-full bg-gold px-1.5 text-[10px] font-extrabold text-ink" aria-label={`player ${slot + 1}`}>
                        P{slot + 1}
                      </span>
                    ) : (
                      <span className="text-base leading-none text-ink-soft/50" aria-hidden>
                        +
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(sp)}
                    aria-label={`Edit ${sp.name}`}
                    className={`grid w-8 place-items-center border-l text-xs ${picked ? 'border-white/15 text-cream/70 hover:text-cream' : 'border-ink/10 text-ink-soft/60 hover:text-ink'}`}
                  >
                    ✏️
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="card px-4 py-2" aria-label="Players">
        <ol className="divide-y divide-ink/8" onBlur={() => setTouched(true)}>
          {draft.players.map((p, i) => (
            <PlayerCard
              key={i}
              index={i}
              player={p}
              error={errors[i] ?? null}
              showError={p.name.length > 0 || touched}
              photos={photos}
              onPickSaved={() => {
                setSaved(getSavedPlayers());
                setPickFor(i);
              }}
            />
          ))}
        </ol>
      </section>

      <div className="fixed inset-x-0 bottom-0 bg-gradient-to-t from-felt-950 via-felt-950/90 to-transparent px-4 pt-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto max-w-lg">
          <button type="button" className="btn btn-primary h-14 w-full text-lg" disabled={!valid} onClick={() => navigate('/new/teams')}>
            {valid ? 'Continue to seats & teams →' : `Add players · ${ready}/4`}
          </button>
          {!valid && ready === 4 && <p className="mt-2 text-center text-sm text-cream/75">Give all four players a different name.</p>}
        </div>
      </div>

      <Sheet open={pickFor !== null} onClose={() => setPickFor(null)} title={`Saved players → Player ${(pickFor ?? 0) + 1}`}>
        {saved.length === 0 ? (
          <p className="py-6 text-center text-ink-soft">Players from your previous matches show up here.</p>
        ) : (
          <ul className="grid gap-2">
            {saved
              .slice()
              .reverse()
              .map((s) => {
                const inUse = taken.has(nameKey(s.name)) && nameKey(draft.players[pickFor ?? 0]?.name ?? '') !== nameKey(s.name);
                return (
                  <li key={s.name} className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={inUse}
                      className="flex min-h-12 flex-1 items-center gap-3 rounded-xl p-2 text-left ring-1 ring-ink/10 hover:bg-ink/5 disabled:opacity-40"
                      onClick={() => {
                        setPlayer(pickFor!, { name: s.name, avatarKey: s.avatarKey, playerId: s.playerId });
                        setPickFor(null);
                      }}
                    >
                      <Avatar name={s.name} avatarKey={s.avatarKey} size={40} />
                      <bdi dir="auto" className="name flex-1 font-semibold">
                        {s.name}
                      </bdi>
                      {inUse && <span className="text-xs">already added</span>}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-ink min-h-10 px-3 text-sm"
                      aria-label={`Edit ${s.name}`}
                      onClick={() => setEditing(s)}
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-ink min-h-10 px-3 text-sm"
                      aria-label={`Forget ${s.name}`}
                      onClick={() => forgetEdit(s.name)}
                    >
                      ✕
                    </button>
                  </li>
                );
              })}
          </ul>
        )}
      </Sheet>

      <EditPlayerSheet
        player={editing}
        onClose={() => setEditing(null)}
        onSave={(patch) => saveEdit(editing!.name, patch)}
        onForget={() => forgetEdit(editing!.name)}
        photos={photos}
        nameTaken={(name) => saved.some((p) => p.name !== editing?.name && nameKey(p.name) === nameKey(name))}
      />

      <Sheet
        open={renaming !== null}
        onClose={() => setRenaming(null)}
        title="Rename saved team"
        footer={
          <button
            type="button"
            className="btn btn-ink w-full"
            onClick={() => {
              if (!renaming) return;
              renameSavedTeam(teamSetKey(renaming.players), renameLabel.trim() || renaming.label);
              setSavedTeams(getSavedTeams());
              setRenaming(null);
            }}
          >
            Save
          </button>
        }
      >
        {renaming && (
          <div>
            <label className="label" htmlFor="team-label">
              Name for this lineup
            </label>
            <input
              id="team-label"
              value={renameLabel}
              onChange={(e) => setRenameLabel(e.target.value)}
              maxLength={40}
              className="field"
              autoFocus
            />
          </div>
        )}
      </Sheet>
    </main>
  );
}
